import { defineDocument, type LineGridDef } from './types.js'
import {
  ALL_TABS, BASIC_ACTIONS, FULL_ACTIONS, baseColumns, baseFilters, col, f, jobType, taxUnit,
} from './common.js'

/**
 * The BOQ, and the estimate underneath it.
 *
 * A BOQ line is what the customer ordered and at what price. An estimation
 * line is what we think it will cost us to deliver that BOQ line, broken into
 * material, labour, bundled work and lump sums. Everything the business wants
 * to know — am I making money on this job, what is left to buy, what may I
 * still bill — is a comparison between those two, which is why they are
 * modelled as two grids on one document rather than as free text.
 */

const COST_TYPES = [
  { value: 'MATERIAL', label: 'Material' },
  { value: 'LABOUR', label: 'Labour' },
  { value: 'BUNDLED', label: 'Bundled — material and labour together' },
  { value: 'LUMPSUM', label: 'Lump sum' },
  { value: 'EXPENSE', label: 'Expense' },
] as const

const YES_NO = [{ value: 'YES', label: 'Yes' }, { value: 'NO', label: 'No' }] as const

/** docs/05 §1 §BOQ — section headings and priced items, in one tree. */
const boqGrid: LineGridDef = {
  section: 'BOQ',
  title: 'Bill of quantities', n: '03',
  addLabel: 'Add a BOQ line',
  columns: [
    col.seq(),
    { key: 'boqCode', label: 'Ref no', type: 'text', width: 104, editable: true,
      hint: 'The reference the customer uses. Sub-items read 12.1, 86 - 1.a.' },
    { key: 'clientRef', label: 'Client ref', type: 'text', width: 104, editable: true },
    { key: 'lineType', label: 'Kind', type: 'text', width: 150, editable: true,
      options: [
        { value: 'SECTION', label: 'Section heading' },
        { value: 'ITEM', label: 'Priced item' },
      ] },
    col.item('Description of work'),
    col.desc(),
    { key: 'quantity', label: 'Order qty', type: 'qty', width: 108, align: 'right', editable: true },
    { key: 'designQuantity', label: 'Design qty', type: 'qty', width: 108, align: 'right', editable: true,
      hint: 'What engineering says is actually needed. Left blank it follows the order quantity.' },
    { key: 'rateOnly', label: 'Rate only', type: 'text', width: 108, editable: true, options: [...YES_NO],
      hint: 'A priced line with no fixed quantity. Nothing raised against it can ever exceed it.' },
    col.unit(),
    { key: 'supplyRate', label: 'Supply rate', type: 'rate', width: 118, align: 'right', editable: true },
    { key: 'rate', label: 'Installation rate', type: 'rate', width: 132, align: 'right', editable: true },
    { key: 'tendered', label: 'Tendered', type: 'text', width: 108, editable: true, options: [...YES_NO],
      hint: 'No marks extra work outside the original tender. It is reconciled through Regularise BOQ.' },
    { key: 'billingTermLineId', label: 'Billing term', type: 'text', width: 190, editable: true,
      parentPicker: true, picksFrom: 'BILLING_TERM' },
    // docs/15 R-3.11 — per BOQ line, not per tenant. The company setting is the
    // default; a line with several billable estimates under it can need the
    // other method, and Billing Reconciliation reads the line's own answer.
    { key: 'progressMethod', label: 'Progress method', type: 'text', width: 170, editable: true,
      options: [
        { value: 'CUMULATIVE', label: 'Cumulative — total done to date' },
        { value: 'MINIMUM', label: 'Minimum — the least complete of its estimates' },
      ],
      hint: 'Left empty it follows the company setting.' },
    { key: 'summaryItem', label: 'Summary item', type: 'text', width: 170, editable: true,
      hint: 'The billing summary line this rolls into on the customer’s own summary.' },
    /*
      Which half of quantity x rate the billing term's share comes off.

      Yes — the default, and what every line written before this does — bills
      30 of 100 units at the full rate under a 30% term. No bills all 100 at
      30% of the rate, which is how some contracts word the same term. It used
      to be a column read by nothing, above a hint pointing at a project
      setting that does not exist.
    */
    { key: 'applyTermToQuantity', label: 'Term on quantity', type: 'text', width: 150, editable: true,
      options: [...YES_NO],
      hint: 'Yes takes the term’s share off the quantity billed. No bills the whole quantity at that share of the rate.' },
    col.hsn(),
    col.tax(),
    col.amount(),
  ],
  // One spreadsheet carries the BOQ and the estimate under it (docs/13), so
  // both grids offer the same import and it lands in both.
  sources: [{ key: 'excel', label: 'Import the BOQ and estimate from Excel', source: 'excel' }],
}

/** docs/05 §1 §Estimation — internal cost, hung off a BOQ line. */
const estimationGrid: LineGridDef = {
  section: 'ESTIMATION',
  parentSection: 'BOQ',
  title: 'Estimation', n: '04',
  addLabel: 'Add an estimation line',
  totalsAs: 'NONE',
  columns: [
    col.seq(),
    { key: 'parentLineId', label: 'Against BOQ line', type: 'text', width: 220, editable: true, parentPicker: true,
      hint: 'Leave it empty for a common item or an overhead that belongs to no one BOQ line.' },
    { key: 'boqCode', label: 'Est ref', type: 'text', width: 92, editable: true },
    col.item('Estimated item'),
    col.desc(),
    { key: 'costType', label: 'Cost type', type: 'text', width: 170, editable: true, options: [...COST_TYPES] },
    { key: 'quantityMultiplier', label: 'Per BOQ unit', type: 'qty', width: 116, align: 'right', editable: true,
      hint: 'How much of this goes into one unit of the BOQ line. Quantity is worked out from it.' },
    col.qty('Estimated qty'),
    col.unit(),
    col.rate('Estimated rate'),
    { key: 'isBillable', label: 'Billable', type: 'text', width: 108, editable: true, options: [...YES_NO] },
    { key: 'allowedBrands', label: 'Allowed brands', type: 'text', width: 180, editable: true,
      hint: 'Comma separated. Buying outside this list raises an exception.' },
    // docs/24 §3.3 — when the site needs it. Left empty the schedule decides.
    { key: 'requiredBy', label: 'Needed on site by', type: 'date', width: 150, editable: true,
      hint: 'Left empty, the start of the schedule task for this BOQ line or its section, then the project’s planned start.' },
    col.amount('Estimated cost'),
  ],
  sources: [{ key: 'excel', label: 'Import the estimate from Excel', source: 'excel' }],
}

/**
 * docs/05 §1 §Billing Details — how one BOQ line is billed in slices.
 *
 * Rows are the term lines themselves: a name, which half of the value they cut
 * from, their share of it, and whether the share is released by supply or by
 * installation. A BOQ line then points at the one it is billed under.
 */
const billingTermGrid: LineGridDef = {
  section: 'BILLING_TERM',
  title: 'Billing terms', n: '02',
  addLabel: 'Add a billing term line',
  totalsAs: 'NONE',
  columns: [
    col.seq(),
    { key: 'termName', label: 'Term', type: 'text', width: 160, editable: true,
      hint: 'Lines sharing a name are one term. Their percentages must add to 100.' },
    { key: 'description', label: 'Billing line', type: 'text', editable: true },
    { key: 'termSection', label: 'Cuts from', type: 'text', width: 160, editable: true,
      options: [
        { value: 'SUPPLY', label: 'Supply value' },
        { value: 'INSTALLATION', label: 'Installation value' },
      ] },
    { key: 'percentage', label: 'Percentage', type: 'rate', width: 120, align: 'right', editable: true },
    { key: 'linkedTo', label: 'Released by', type: 'text', width: 160, editable: true,
      options: [
        { value: 'NONE', label: 'Nothing — billed on its own' },
        { value: 'SUPPLY', label: 'Supply' },
        { value: 'INSTALLATION', label: 'Installation' },
      ] },
  ],
  sources: [],
}

/**
 * docs/05 §1 §Billing Summary Items — how a bill is grouped for the customer.
 *
 * "Fire Hydrant System", "Fire Sprinkler Items": the headings a customer sees
 * on their own summary page, which BOQ lines roll into. Kept as its own grid
 * rather than as free text on the line, so the same heading spelt two ways
 * cannot become two headings.
 */
const summaryItemGrid: LineGridDef = {
  section: 'SUMMARY_ITEM',
  title: 'Billing summary items', n: '05',
  addLabel: 'Add a summary item',
  totalsAs: 'NONE',
  columns: [
    col.seq(),
    { key: 'description', label: 'Summary item', type: 'text', editable: true,
      hint: 'The heading this group of BOQ lines is billed under.' },
  ],
  sources: [],
}

/** docs/05 §1 — the contract, and the BOQ everything downstream is attributed to. */
export const project = defineDocument({
  key: 'project',
  group: 'Project',
  blurb: 'Create projects with BOQ information. Add detailed cost estimation for all BOQ lines.',
  route: 'projects',
  label: 'Project',
  labelPlural: 'Projects',
  numbering: 'PROJECT',
  numberPrefix: 'PRO/',
  numberWidth: 3,
  workflow: 'PROJECT',
  tabs: ['summary', 'related', 'revisions', 'form', 'drive'],
  createWizard: {
    title: 'Project setup',
    subtitle: 'These four settings are decided once and cannot be changed afterwards.',
    fields: [
      f.radio('pricingStyle', 'Pricing style', [['TWO_RATES', 'Two rates — supply and installation priced separately'], ['SINGLE_RATE', 'Single rate']]),
      f.radio('billingMethod', 'Billing method', [['BOQ_LEVEL', 'BOQ level'], ['PROJECT_LEVEL', 'Project level']]),
      f.radio('trackSiteInventory', 'Track inventory at the site?', [['YES', 'Yes'], ['NO', 'No']]),
      { ...taxUnit(true), width: 'full' as const },
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.text('name', 'Project name', 'full', true),
        f.look('customerId', 'Customer', 'customer', true),
        f.date('documentDate', 'Document date', true),
        // Set once in the wizard and shown here, locked, so the promise the
        // wizard makes is one the form keeps.
        { ...f.choice('pricingStyle', 'Pricing style', [['TWO_RATES', 'Two rates'], ['SINGLE_RATE', 'Single rate']], false), lockAfterDraft: true },
        { ...f.choice('billingMethod', 'Billing method', [['BOQ_LEVEL', 'BOQ level'], ['PROJECT_LEVEL', 'Project level']], false), lockAfterDraft: true },
        { ...f.choice('trackSiteInventory', 'Track inventory at the site?', [['YES', 'Yes'], ['NO', 'No']], false), lockAfterDraft: true },
        f.text('customerPoNo', 'Customer PO no'),
        // docs/05 §1 — the customer's own reference for this job, which is what
        // their accounts department quotes back at you.
        f.text('externalId', 'External id'),
        f.text('consignee', 'Consignee'),
        f.date('customerPoDate', 'Customer PO date'),
        f.look('projectManagerId', 'Project manager', 'employee'),
        f.look('masterProjectId', 'Master project', 'project'),
        f.choice('linkType', 'Link to the master project as', [['PHASE', 'A phase'], ['SUB_PROJECT', 'A sub-project'], ['VARIATION', 'A variation']], false),
        jobType(),
        taxUnit(),
        f.date('plannedStart', 'Planned start'),
        f.date('plannedFinish', 'Planned finish'),
        { key: 'documentNo', label: 'Project number', control: 'text', width: 'half',
          requiresSetting: 'manual_numbering_projects',
          hint: 'Left empty the next number in the series is used.' },
        f.notes(),
      ] },
      // docs/05 §1 §Billing Details — the two answers that change how a BOQ
      // line is billed, which is why they sit with the billing terms rather
      // than with the addresses.
      { key: 'billing', n: '02', title: 'Billing details', fields: [
        { ...f.choice('billingMethod', 'Billing method', [['BOQ_LEVEL', 'BOQ level'], ['PROJECT_LEVEL', 'Project level']], false), lockAfterDraft: true },
        f.yesNo('allowBaseRateAdjustment', 'Allow the base rate to be adjusted?',
          'Lets a billed line be raised at a rate other than the one on the BOQ.'),
        f.yesNo('applyBillingTermToQuantity', 'Apply the billing term to the quantity?',
          'On: a term of 70% bills 70% of the quantity. Off: it bills 70% of the value.'),
      ] },
      { key: 'site', n: '03', title: 'Site information', fields: [
        f.look('siteAddressId', 'Site address', 'address'),
        f.look('siteContactId', 'Site contact', 'contact'),
        f.look('billingAddressId', 'Billing address', 'address'),
        f.look('billingContactId', 'Billing contact', 'contact'),
      ] },
    ],
  },
  lines: boqGrid,
  grids: [billingTermGrid, estimationGrid, summaryItemGrid],
  columns: [
    { key: 'documentNo', label: 'Project no', type: 'docno', width: 132 },
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'customerName', label: 'Customer', type: 'text' },
    { key: 'documentDate', label: 'Started', type: 'date', width: 128 },
    { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
    { key: 'total', label: 'Project value', type: 'money', width: 156, align: 'right' },
  ],
  filters: baseFilters('customer', 'Customer'),
  actions: FULL_ACTIONS,
  rules: ['billing_term_percentages'],
  createsNext: [
    { docType: 'material_request', label: 'Material request', whenStatus: 'APPROVED' },
    { docType: 'purchase_order', label: 'Purchase order', whenStatus: 'APPROVED' },
    { docType: 'billing_request', label: 'Billing request', whenStatus: 'APPROVED' },
  ],
} as const)

/** docs/05 §3 — what the site asks for, drawn from the estimate. */
export const materialRequest = defineDocument({
  key: 'material_request',
  group: 'Project',
  blurb: 'Request materials required for the project. Pick the material from the detailed estimation.',
  route: 'mat_reqs',
  label: 'Material request',
  labelPlural: 'Material requests',
  numbering: 'MATERIAL_REQUEST',
  numberPrefix: 'MR/',
  workflow: 'MATERIAL_REQUEST',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Create a material request',
    subtitle: 'Choose the project. You can also copy another request afterwards.',
    fields: [f.look('projectId', 'Project', 'project', true, 'full')],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.look('projectId', 'Project', 'project', true),
        f.look('customerId', 'Customer', 'customer'),
        f.date('documentDate', 'Document date', true),
        f.date('requiredDate', 'Required by', true),
        f.look('requestedById', 'Requested by', 'employee'),
        jobType(),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Materials required', n: '02',
    columns: [
      col.seq(), col.item(), col.desc(),
      { key: 'allowedBrands', label: 'Allowed brands', type: 'text', width: 160 },
      col.qty('Requested'), col.unit(),
      /*
        What the stores team decided to actually buy.

        "Stores team can decide the purchase quantity (out of requested) based
        on the stock already available in the warehouse." The request says what
        the site wants; this says what procurement should go and get, and the
        two are different every time there is stock on the shelf. Left empty it
        is the requested quantity (docs/video-notes/06 §9).
      */
      { key: 'plannedQty', label: 'To buy', type: 'qty', width: 110, align: 'right', editable: true,
        hint: 'Left empty, the whole requested quantity is bought.' },
      { key: 'isBillable', label: 'Type', type: 'text', width: 130, editable: true,
        options: [{ value: 'YES', label: 'Billable' }, { value: 'NO', label: 'Non billable' }] },
      { key: 'forProjectId', label: 'For', type: 'text', width: 180, editable: true, lookup: { entity: 'project' },
        hint: 'Left empty it is for this project. Set it when you are buying on behalf of another job.' },
      col.boqRef(),
    ],
    sources: [
      { key: 'estimation', label: 'Pick from the estimate', source: 'estimation' },
      { key: 'excel', label: 'Import from Excel', source: 'excel' },
    ],
  },
  columns: baseColumns(null, 'Requested value'),
  filters: baseFilters(null),
  actions: [...BASIC_ACTIONS, 'preClose', 'undoPreClose'],
  rules: ['excess_purchase_quantity'],
  createsNext: [
    // docs/24 §3.4 — the purchase executive's step between the site and the order.
    { docType: 'purchase_requisition', label: 'Purchase requisition', whenStatus: 'APPROVED' },
    { docType: 'purchase_order', label: 'Purchase order', whenStatus: 'APPROVED' },
    { docType: 'material_transfer', label: 'Material transfer', whenStatus: 'APPROVED' },
  ],
} as const)
